import { RemoveMemberSuccessDescription } from "@components/remove-member-success-description";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { SdbMemberTypes } from "@custom-types/member-types";
import { BaseTeamProps } from "@custom-types/teams-types";
import { DeleteIcon, Dialog, MenuItem, Text, useToast } from "@flat-ui";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/faro-dialog";
import { UserDisplay } from "@components/common/user-display";
import { fetchingMembersFlagsSelector } from "@store/members/members-selector";
import { removeMemberFromCompany } from "@store/members/members-slice-thunk";
import { selectedSdbCompanyNameSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { fetchingTeamsFlagsSelector } from "@store/teams/teams-selector";
import { removeMembersFromTeam } from "@store/teams/teams-slice-thunk";
import {
  TableType,
  TeamMemberEvents,
  WorkspaceMemberEvents,
} from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";

/** Determines from where a member should be removed */
type RemoveTarget = "team" | "workspace";

interface Props extends BaseCompanyIdProps, BaseTeamProps {
  /** The member to remove */
  member: SdbMemberTypes;

  /** Callback to close the parent 3-dot menu */
  onMenuClose: () => void;
}

/**
 * Menu button that opens a sub-menu with two removal options:
 * "From team" and "From workspace".
 * Selecting either opens a confirmation dialog.
 */
export function TeamRemoveMenuButton({
  companyId,
  team,
  member,
  onMenuClose,
}: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const { openToast } = useToast();
  const { trackEvent } = useTrackEvent();
  const companyName = useAppSelector(selectedSdbCompanyNameSelector);

  const [removeTarget, setRemoveTarget] = useState<RemoveTarget | null>(null);
  const { isRemovingTeamMember } = useAppSelector(fetchingTeamsFlagsSelector);
  const { isRemovingCompanyMember } = useAppSelector(
    fetchingMembersFlagsSelector
  );

  const isRemoving =
    removeTarget === "team" ? isRemovingTeamMember : isRemovingCompanyMember;

  function closeDialog(): void {
    setRemoveTarget(null);
    onMenuClose();
  }

  async function confirmRemove(): Promise<void> {
    try {
      switch (removeTarget) {
        case "team":
          await removeFromTeam();
          break;
        case "workspace":
          await removeFromWorkspace();
          break;
      }
    } catch {
      // Errors are handled by the respective slices
    }
  }

  async function removeFromTeam(): Promise<void> {
    trackEvent({
      name: TeamMemberEvents.removeMemberFromTeam,
      props: { dataType: TableType.teamMembers },
    });

    try {
      await dispatch(
        removeMembersFromTeam({
          companyId,
          teamId: team.id,
          memberIds: [member.identity],
        })
      ).unwrap();

      openToast({
        title: `Member removed from ${TEAM_DISPLAY_NAME}`,
        message: (
          <RemoveMemberSuccessDescription
            member={member}
            subjectType="team"
            entityName={team.name}
          />
        ),
      });
    } finally {
      closeDialog();
    }
  }

  async function removeFromWorkspace(): Promise<void> {
    trackEvent({
      name: WorkspaceMemberEvents.removeMember,
      props: { dataType: TableType.teamMembers },
    });

    try {
      await dispatch(removeMemberFromCompany({ companyId, member })).unwrap();

      openToast({
        title: "Member removed from workspace",
        message: (
          <RemoveMemberSuccessDescription
            member={member}
            subjectType="workspace"
            entityName={companyName ?? ""}
          />
        ),
      });
    } finally {
      closeDialog();
    }
  }

  const removeTargetDisplayName =
    removeTarget === "team" ? TEAM_DISPLAY_NAME : "workspace";

  return (
    <>
      <MenuItem label="Remove member" Icon={DeleteIcon} subMenuTrigger="click">
        <MenuItem
          label={`From ${TEAM_DISPLAY_NAME}`}
          onClick={() => setRemoveTarget("team")}
        />
        <MenuItem
          label="From workspace"
          onClick={() => setRemoveTarget("workspace")}
        />
      </MenuItem>

      <Dialog
        title={`Remove from ${removeTargetDisplayName}`}
        confirmText="Remove"
        open={removeTarget !== null}
        onConfirm={confirmRemove}
        onCancel={closeDialog}
        showSpinner={isRemoving}
      >
        <Text
          variant="bodyM"
          sx={{
            marginBottom: SPACE_ELEMENTS_OF_MODAL,
          }}
        >
          When removing member from {removeTargetDisplayName} they will no
          longer be able to access this {removeTargetDisplayName}. You can
          always invite this member again.
        </Text>

        <UserDisplay member={member} />
      </Dialog>
    </>
  );
}
